import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { CnResult } from '@cainiaofe/cn-ui';
import { CnChart } from '@/components/cn-chart';
import {
  calculateAdvancedConfig,
  calculateFeature,
  getChartCommonStyle,
  isDesignMode,
} from '@/lowcode/common/util/util';
import {
  getPieCharOptions,
  getPieFields,
  getRealChartStyle,
  getPieLegendChangeCallback,
} from '@/lowcode/common/comoponents/cn-pie-chart/util';
import { CnPieChartProps } from '@/lowcode/common/comoponents/cn-pie-chart/type';
import useFilterSearchListener from '../common/hooks/useFilterSearchListener';
import { FeaturePosition } from '@/types/feature-position';
import useStopLoop from '@/lowcode/common/hooks/useStopLoop';
import { useData } from '@/lowcode/common/hooks/useData';
import { EmptyData } from '../common/empty-data';
import WrapperContainer from '../common/wrapper-container';
import { useLegendChange } from '@/lowcode/common/hooks/useLegendChange';

const CnPieChart = forwardRef(
  (props: CnPieChartProps, ref: React.ForwardedRef<{ load: () => void }>) => {
    const {
      dataFrom,
      filterConfig,
      chartStyle: originalChartStyle,
      _nodeId,
      forceRequest = false,
      _context,
      __featureConfig__,
    } = props || {};

    const echartRef = useRef<echarts.ECharts>(null);
    const { requestConfig } = dataFrom || {};
    const pieField = getPieFields(requestConfig);
    const { angleField, colorField } = pieField;
    const realAngleField = angleField?.[0]?.fieldName; // 数值字段
    const realColorField = colorField?.[0]?.fieldName; // 分类字段
    const { _bindFilter } = filterConfig || {};
    const chartStyle = getRealChartStyle(originalChartStyle, __featureConfig__);

    const {
      data: listData,
      error,
      loading,
      refreshData: sendRequest,
    } = useData({
      config: { ...props, isDesign: isDesignMode(props) },
      source: 'chart',
    });

    useImperativeHandle(ref, () => ({
      load: sendRequest,
    }));

    useFilterSearchListener({
      _bindFilter,
      sendRequest,
      _context,
    });

    useStopLoop({
      _nodeId,
    });

    useEffect(() => {
      if (forceRequest) {
        sendRequest();
      }
    }, [forceRequest]);

    useLegendChange(echartRef, {
      deps: listData || [],
      legendChangeCallback: getPieLegendChangeCallback(chartStyle, realColorField, realAngleField),
    });

    if (!listData || !realAngleField || !realColorField) {
      return (
        <EmptyData
          height={chartStyle.height}
          emptyImg={
            'https://img.alicdn.com/imgextra/i1/O1CN01FNbAOm1CykIuh3bv4_!!6000000000150-2-tps-500-260.png'
          }
        />
      );
    }
    const renderMain = () => {
      if (!listData?.length) {
        return <CnResult type="empty" title="数据为空" subTitle="暂无数据" />;
      }
      const defaultOptions = {
        ...getPieCharOptions(listData, pieField, chartStyle),
      };

      const finalOptions = calculateAdvancedConfig({
        props,
        options: calculateFeature({
          props,
          options: defaultOptions,
          position: FeaturePosition.pie,
          sendRequest,
        }),
        state: _context?.state,
      });

      return <CnChart ref={echartRef} {...getChartCommonStyle(props)} {...finalOptions} />;
    };

    return (
      <WrapperContainer loading={loading} error={error}>
        {renderMain()}
      </WrapperContainer>
    );
  },
);

CnPieChart.displayName = 'CnPieChartPro';
export default CnPieChart;

export { CnPieChart };
